<script setup lang="ts">
import { Loader } from "@googlemaps/js-api-loader";
import {
  MarkerClusterer,
  SuperClusterAlgorithm
} from "@googlemaps/markerclusterer";
import type { IGeolocalization } from "~/types/components";
import colorMapStyle from "./colorMapStyle";
import paletteByTheme from "./paletteByTheme";

const props = defineProps<{
  items: Array<IGeolocalization>;
  isLoading: boolean;
  hasError: boolean;
}>();
const emit = defineEmits(["error"]);

const noDataAvailable = computed(() => {
  return props.items.length === 0 && !props.isLoading && !props.hasError;
});

// Fetching the Google Maps API key from runtime configuration
const apiKey = useRuntimeConfig().public.googleMapsApiKey;
const mapContainer = ref<HTMLElement | null>(null);
const map = ref<google.maps.Map | null>(null);

// Access to color palette for cluster styles
const { colorPalette: theme } = useMain();
const palette = paletteByTheme[theme as keyof typeof paletteByTheme];

// MarkerCluster object for managing clusters
let clusterer: MarkerClusterer | null = null;

// Function to initialize the Google Map
const initializeMap = async () => {
  const loader = new Loader({
    apiKey: apiKey,
    version: "weekly"
  });

  const google = await loader.load();

  if (!mapContainer.value) return;

  map.value = new google.maps.Map(mapContainer.value, {
    center: { lat: 20, lng: 0 }, // Initial map center
    zoom: 2,
    maxZoom: 18,
    minZoom: 2,
    streetViewControl: false,
    styles: colorMapStyle // Applying a custom style to the map
  });
};

// Function to update map markers when data changes
const updateMarkers = () => {
  if (!map.value) return;

  if (clusterer) {
    clusterer.clearMarkers(); // Clear existing clusters to refresh
  }

  // Creating individual markers for each item
  const markers = props.items.map((location) => {
    // Customize marker icons using SVG encoded as data URL
    const marker = new google.maps.Marker({
      position: {
        lat: location.latitude,
        lng: location.longitude
      },
      map: map.value as google.maps.Map,
      clickable: false,
      icon: {
        url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
          `<svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 48 48"
            fill="none">
            <path
              d="M36.4981 14.388C35.3099 11.485 33.1498 9.08511 30.3873 7.5991C27.6249 6.11309 24.4319 5.63327 21.3547 6.24183C18.2775 6.85039 15.5075 8.50953 13.5186 10.9352C11.5298 13.3608 10.4458 16.4023 10.4521 19.539C10.4671 28.44 17.0911 35.1931 23.1181 41.6281C23.2302 41.7461 23.3651 41.84 23.5147 41.9042C23.6642 41.9684 23.8253 42.0015 23.9881 42.0015C24.1508 42.0015 24.3119 41.9684 24.4615 41.9042C24.611 41.84 24.746 41.7461 24.8581 41.6281C30.2281 35.8951 36.0301 29.775 37.2511 22.341C37.8256 19.6758 37.5625 16.8981 36.4981 14.388ZM24.0001 25.638C22.7938 25.638 21.6146 25.2803 20.6116 24.6101C19.6087 23.94 18.8269 22.9874 18.3653 21.873C17.9037 20.7585 17.7829 19.5322 18.0182 18.3491C18.2536 17.166 18.8345 16.0793 19.6874 15.2264C20.5404 14.3734 21.6271 13.7925 22.8102 13.5572C23.9933 13.3218 25.2196 13.4426 26.3341 13.9042C27.4485 14.3659 28.401 15.1476 29.0712 16.1506C29.7414 17.1536 30.0991 18.3328 30.0991 19.539C30.1002 20.3407 29.9434 21.1347 29.6374 21.8757C29.3314 22.6167 28.8824 23.2902 28.3159 23.8575C27.7494 24.4248 27.0767 24.8748 26.3361 25.1819C25.5956 25.489 24.8017 25.647 24.0001 25.647V25.638Z"
              fill="${palette["30"]}" />
          </svg>`
        )}`,

        scaledSize: new google.maps.Size(32, 32), // Size of marker
        anchor: new google.maps.Point(16, 16) // Anchor point for marker icon
      }
    });
    return marker;
  });

  // Creating a new clusterer with custom rendering logic for clusters
  clusterer = new MarkerClusterer({
    map: map.value,
    markers: markers,
    algorithm: new SuperClusterAlgorithm({
      radius: 80, // Determines cluster radius
      maxZoom: 16,
      minZoom: 2
    }),
    renderer: {
      render: ({ count, position }) => {
        // Custom rendering logic based on the number of points
        const { size, color, divider } = getClusterConfig(count);
        let label = count.toString();
        if (count >= 1000) {
          label = (count / 1000).toFixed(1) + " K";
        }
        return new google.maps.Marker({
          position,
          icon: {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
              `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="${size / 2}" cy="${size / 2}" r="${
                  size / 2
                }" fill="#ffffff66"/>
                <circle cx="${size / 2}" cy="${size / 2}" r="${
                  size / divider
                }" fill="${color}"/>
                <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-family="Segoe UI" font-size="14px" font-weight="500">${label}</text>
              </svg>`
            )}`,
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2) // Center of the cluster icon
          }
        });
      }
    }
  });
};

// Configuration function for cluster styles based on count
const getClusterConfig = (count: number): ClusterConfig => {
  if (count < 10) {
    return {
      size: 53,
      divider: 3.2,
      color: palette["30"]
    };
  }
  if (count < 100) {
    return {
      size: 56,
      divider: 3.1,
      color: palette["40"]
    };
  }
  if (count < 1000) {
    return {
      size: 66,
      divider: 3,
      color: palette["50"]
    };
  }
  if (count < 10000) {
    return {
      size: 78,
      divider: 2.9,
      color: palette["60"]
    };
  }
  return {
    size: 90,
    divider: 2.8,
    color: palette["70"]
  };
};

onMounted(async () => {
  await initializeMap();
  updateMarkers();
});

watch(
  () => props.items,
  () => {
    updateMarkers();
  },
  { deep: true }
);
</script>
<template>
  <ImpactLoading v-if="isLoading" />
  <ImpactError v-else-if="hasError" @error="emit('error')" />
  <ImpactNoData v-else-if="noDataAvailable" />
  <div v-else ref="mapContainer" class="ImpactMap"></div>
</template>

<style lang="scss">
.ImpactMap {
  height: 100%;
  width: 100%;
  min-height: 400px;
  max-height: 400px;
  overflow: hidden;
  margin-bottom: 32px;

  .mapdiv {
    min-height: 400px;
  }

  /* Adjust scaling and styling of Google Maps controls */
  .gmnoprint.gm-style-mtc-bbw {
    transform: scale(0.9);
    transform-origin: top left;
  }

  .gmnoprint {
    transform: scale(0.9);
    transform-origin: top right;
  }

  .gm-svpc {
    transform: scale(0.9) translateY(-60px);
    transform-origin: top right;

    & > div {
      height: 80px;
    }
  }
}
</style>
